<template>
  <panel :title="tournament.name">
    <template v-slot:actions>
      <el-tooltip
        v-if="user.isAdmin"
        :content="$tc('general.print')"
        placement="top"
      >
        <el-button
          icon="el-icon-printer"
          size="medium"
          circle
          class="mr-10"
          @click="showPrintModal = true"
        />
      </el-tooltip>

      <el-button
        v-if="user.isAdmin"
        size="medium"
        round
        class="mr-10"
        @click="showDocumentPrintModal = true"
        >{{ $tc("tournament.print_documents") || "Print Documents" }}</el-button
      >

      <el-dropdown
        v-if="user.isAdmin"
        trigger="click"
        @command="handleCommand"
        @visible-change="handleDropdownVisible"
      >
        <el-button type="primary" icon="el-icon-more" circle />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="edit"
            ><i class="el-icon-edit" />{{
              $tc("tournament.edit_tournament")
            }}</el-dropdown-item
          >
          <el-dropdown-item command="edit_club"
            ><i class="el-icon-edit" />{{
              $tc("tournament.edit_club_name")
            }}</el-dropdown-item
          >
          <el-dropdown-item
            :command="tournament.registrationOpen ? 'close' : 'open'"
          >
            <i class="el-icon-edit" />{{
              tournament.registrationOpen
                ? $tc("tournament.close_applications")
                : $tc("tournament.open_applications")
            }}
          </el-dropdown-item>
          <el-dropdown-item command="registration_fee"
            ><i class="el-icon-tickets" />{{
              $tc("general.registration_fee")
            }}</el-dropdown-item
          >
          <el-dropdown-item command="assign_numbers"
            ><i class="el-icon-edit-outline" />{{
              $tc("tournament.assign_numbers")
            }}</el-dropdown-item
          >
        </el-dropdown-menu>
      </el-dropdown>
    </template>

    <div v-if="tournament.cover" class="cover">
      <img :src="$file('tournaments', tournament.cover, tournament.id)" />
    </div>

    <div v-if="tournament.images.length" class="gallery">
      <div v-for="image in tournament.images" :key="image.id" class="image">
        <img
          :src="$file('images', image.path)"
          @click="openPreviewImage(image.path)"
        />
        <div v-if="user.isAdmin" class="delete" @click="askDeleteImage(image)">
          <i class="el-icon-delete" />
        </div>
      </div>
    </div>

    <h4>{{ $tc("form.description") }}:</h4>
    <div class="description" v-html="tournament.description" />

    <template v-if="tournament.documents.length">
      <h4>{{ $tc("general.documents") }}:</h4>

      <div class="documents">
        <file
          v-for="document in tournament.documents"
          class="document"
          :key="document.id"
          :file="document"
          :url="$file('documents', document.name)"
          :deletable="user.isAdmin"
          @delete="onDeleteDocument(document)"
        />
      </div>
    </template>

    <manage-change v-if="openManageClub" ref="manageChange" @close="close()" />

    <el-dialog :visible.sync="galleryVisible" width="90%">
      <img style="max-width: 100%" :src="$file('images', previewImage)" />
    </el-dialog>

    <!-- Print Components (hidden) -->
    <PrintRegistrationFeeByClub
      v-if="tournament"
      v-show="false"
      ref="printRegistrationFeeByClub"
      :tournament="tournament"
      :competitors="competitors"
      :teams="teams"
    />
    <PrintClubsTable
      v-if="clubs && clubs.length > 0"
      v-show="false"
      ref="printClubsTable"
      :data="clubs"
      :tournament-competitors="allData"
    />
    <PrintCompetitorsTable
      v-if="competitors"
      v-show="false"
      ref="printCompetitorsTable"
      :data="competitors"
    />
    <PrintTeamsTable
      v-if="teams"
      v-show="false"
      ref="printTeamsTable"
      :data="teams"
    />
    <PrintCoachesTable
      v-if="coaches && coaches.length > 0"
      v-show="false"
      ref="printCoachesTable"
      :data="coaches"
      :show-club-name="true"
    />
    <PrintRefereesTable
      v-if="referees && referees.length > 0"
      v-show="false"
      ref="printRefereesTable"
      :data="referees"
      :show-club-name="true"
    />
    <PrintOfficialsTable
      v-if="officials && officials.length > 0"
      v-show="false"
      ref="printOfficialsTable"
      :data="officials"
      :show-club-name="true"
    />
    <PrintInfoData
      v-if="infoData && infoData.length > 0"
      v-show="false"
      ref="printInfoData"
      :data="infoData"
    />
    <tournament-print-options-modal
      v-model="showPrintModal"
      :tournament="tournament"
      :competitors="competitors"
      :teams="teams"
      :clubs="clubs"
      :coaches="coaches"
      :referees="referees"
      :officials="officials"
      :info-data="infoData"
      @print="handlePrint"
    />

    <document-print-options-modal
      v-model="showDocumentPrintModal"
      :tournament-id="tournament.id"
      @print="handleDocumentPrint"
    />
    <div v-show="false" ref="printDocumentsWrap">
      <print-documents
        v-if="currentDocumentType"
        ref="printDocumentsComponent"
        :data="documentPrintData"
        :document-type="currentDocumentType"
        :target="currentDocumentTarget"
        entity-type="tournament"
        :competition-name="tournament.name"
      />
    </div>
  </panel>
</template>

<script>
import openPrint from "@/common/print";
import errorHandler from "@/common/errorHandler.js";
import escapeHtml from "@/common/escapeHtml.js";

import File from "@/components/files/File";
import Panel from "@/components/panel/Panel";
import ManageChange from "@/components/changeClub/ManageChange";
import PrintRegistrationFeeByClub from "@/components/tournaments/PrintRegistrationFeeByClub";
import TournamentPrintOptionsModal from "@/components/tournaments/TournamentPrintOptionsModal";
import PrintClubsTable from "@/components/clubs/PrintClubsTable";
import PrintCompetitorsTable from "@/components/tournaments/PrintCompetitorsTable";
import PrintTeamsTable from "@/components/tournaments/PrintTeamsTable";
import PrintCoachesTable from "@/components/coaches/PrintCoachesTable";
import PrintRefereesTable from "@/components/referee/PrintRefereesTable";
import PrintOfficialsTable from "@/components/official/PrintOfficialsTable";
import PrintInfoData from "@/components/tournaments/PrintInfoData";
import DocumentPrintOptionsModal from "@/components/documents/DocumentPrintOptionsModal.vue";
import PrintDocuments from "@/components/documents/PrintDocuments.vue";
import { getCompetitors, getCompetitorNames } from "./tournament-discipline";

export default {
  components: {
    File,
    Panel,
    ManageChange,
    PrintRegistrationFeeByClub,
    TournamentPrintOptionsModal,
    PrintClubsTable,
    PrintCompetitorsTable,
    PrintTeamsTable,
    PrintCoachesTable,
    PrintRefereesTable,
    PrintOfficialsTable,
    PrintInfoData,
    DocumentPrintOptionsModal,
    PrintDocuments,
  },

  props: {
    tournament: {
      type: Object,
      default: null,
    },
    competitors: {
      type: Array,
      default: null,
    },
    teams: {
      type: Array,
      default: null,
    },
    clubs: {
      type: Array,
      default: () => [],
    },
    coaches: {
      type: Array,
      default: () => [],
    },
    referees: {
      type: Array,
      default: () => [],
    },
    officials: {
      type: Array,
      default: () => [],
    },
    infoData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      previewImage: null,
      galleryVisible: false,
      openManageClub: false,
      hasBrackets: false,
      checkingBrackets: false,
      showPrintModal: false,
      showDocumentPrintModal: false,
      currentDocumentType: null,
      currentDocumentTarget: null,
      documentPrintData: [],
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    allData() {
      return [
        ...(this.competitors || []),
        ...(this.teams || []).map((item) => ({
          ...item,
          isTeam: true,
        })),
      ];
    },
    allCategories() {
      return this.$store.state.categories.data || [];
    },
  },

  watch: {
    galleryVisible(value) {
      if (!value) this.previewImage = null;
    },
    tournament: {
      handler() {
        // Check bracket existence when tournament changes
        if (this.tournament?.id) {
          this.checkBracketsExistence();
        }
      },
      immediate: true,
    },
  },

  mounted() {
    // Check bracket existence on mount
    if (this.tournament?.id) {
      this.checkBracketsExistence();
    }
  },

  methods: {
    askAssignNumbers() {
      this.$confirm(this.$tc("messages.ask_assign_numbers"), undefined, {
        confirmButtonText: this.$tc("general.yes"),
        cancelButtonText: this.$tc("general.no"),
        type: "warning",
      }).then(this.assignNumbers);
    },

    async assignNumbers() {
      await this.$store.dispatch("tournaments/action", {
        id: this.tournament.id,
        action: "AssignNumbers",
      });
      await this.$store.dispatch("tournaments/get", this.tournament.id);
      this.$message.success(this.$tc("messages.success_assign_numbers"));
    },

    printit() {
      openPrint(
        this.$tc("general.registration_fee"),
        this.$refs.printRegistrationFeeByClub
          ? this.$refs.printRegistrationFeeByClub.$el
          : null
      );
    },

    async askDeleteImage(image) {
      try {
        await this.$confirm("Da li si siguran da želiš da uklonite ovu sliku?");
        this.onDeleteImage(image);
      } catch {}
    },

    async onDeleteImage(image) {
      const loading = this.$loading();
      try {
        await this.$store.dispatch("tournamentImages/delete", image.id);
        this.$store.commit("tournaments/removeImage", {
          tournament: this.tournament,
          id: image.id,
        });
        this.$message.success("Uspešno ste uklonili sliku.");
      } catch (e) {
        console.error(e);
        errorHandler(e);
      } finally {
        loading.close();
      }
    },

    async onDeleteDocument(document) {
      const loading = this.$loading();
      try {
        await this.$store.dispatch("documents/delete", document.id);
        this.$store.commit("tournaments/removeDocument", {
          tournament: this.tournament,
          id: document.id,
        });
        this.$message.success("Uspešno ste uklonili dokument.");
      } catch (e) {
        console.error(e);
        errorHandler(e);
      } finally {
        loading.close();
      }
    },

    openPreviewImage(path) {
      this.previewImage = path;
      this.galleryVisible = true;
    },

    handleCommand(command) {
      if (command === "edit") {
        this.$router.push({
          name: "EditTournament",
          params: { id: this.tournament.id },
        });
      }
      if (command === "edit_club") this.openManageClub = true;
      if (command === "open") this.updateRegistrationStatus(true);
      if (command === "close") this.updateRegistrationStatus(false);
      if (command === "registration_fee") this.printit();
      if (command === "assign_numbers") this.askAssignNumbers();
    },

    async checkBracketsExistence() {
      if (!this.tournament?.id || this.checkingBrackets) return;

      this.checkingBrackets = true;
      try {
        const brackets = await this.$store.dispatch(
          "tournaments/getBrackets",
          this.tournament.id
        );
        this.hasBrackets = brackets && brackets.length > 0;
      } catch (e) {
        // If error, assume no brackets (don't block action)
        this.hasBrackets = false;
      } finally {
        this.checkingBrackets = false;
      }
    },

    handleDropdownVisible(visible) {
      // Check brackets when dropdown is opened
      if (visible && this.tournament?.id) {
        this.checkBracketsExistence();
      }
    },

    async updateRegistrationStatus(status) {
      // Additional frontend check before making the API call
      // if (status === true && this.hasBrackets) {
      //   this.$message.error(
      //     this.$tc("tournament.cannot_reopen_registrations_bracket_exists")
      //   );
      //   return;
      // }

      try {
        await this.$store.dispatch("tournaments/update", {
          id: this.tournament.id,
          payload: {
            registrationOpen: status,
          },
        });

        // IMPORTANT: refetch tournament to refresh UI state
        await this.$store.dispatch("tournaments/get", this.tournament.id);

        if (this.tournament.id === this.$store.state.app.activeTournament?.id) {
          this.$store.commit("app/updateActiveTournamentState", status);
        }
        this.$message.success("Uspešno ste izmenili status prijava.");
      } catch (e) {
        // Handle backend validation error
        const errorMessage =
          e?.response?.data?.error ||
          e?.message ||
          "Greška pri izmeni statusa prijava.";
        this.$message.error(errorMessage);
      }
    },

    close() {
      this.openManageClub = false;
    },

    async handlePrint(selectedOptions) {
      await this.$nextTick();
      const printSections = [];
      const selectedClubIds = selectedOptions.club?.selectedClubIds || [];

      // Support backward compatibility: if selectedClubId exists (old format), convert to array
      const clubIds =
        selectedClubIds.length > 0
          ? selectedClubIds
          : selectedOptions.club?.selectedClubId
            ? [selectedOptions.club.selectedClubId]
            : [];

      // 1. Club-specific prints (only if clubs are selected)
      if (clubIds.length > 0) {
        // Loop through each selected club
        clubIds.forEach((clubId) => {
          const clubIdInt = parseInt(clubId);

          // Club Information
          if (selectedOptions.club.clubInformation) {
            const club = this.clubs?.find((c) => parseInt(c.id) === clubIdInt);
            if (club) {
              const element = this.$refs.printClubsTable?.$el;
              if (element) {
                // Filter to show only selected club
                const clubData = [club];
                const html = this.generateClubTableHtml(clubData);
                const clubName = this.getClubName(clubId);
                printSections.push({
                  title: `${this.$tc("tournament.club_information")}${
                    clubIds.length > 1 ? ` - ${clubName}` : ""
                  }`,
                  html,
                });
              }
            }
          }

          // Club-specific Competitors
          if (selectedOptions.club.competitors) {
            const filteredCompetitors = (this.competitors || []).filter(
              (c) => parseInt(c.club?.id) === clubIdInt
            );
            if (filteredCompetitors.length > 0) {
              const html =
                this.generateCompetitorsTableHtml(filteredCompetitors);
              const clubName = this.getClubName(clubId);
              printSections.push({
                title: `${this.$tc("tournament.competitors")} - ${clubName}`,
                html,
              });
            }
          }

          // Club-specific Teams
          if (selectedOptions.club.teams) {
            const filteredTeams = (this.teams || []).filter(
              (t) => parseInt(t.club?.id) === clubIdInt
            );
            if (filteredTeams.length > 0) {
              const html = this.generateTeamsTableHtml(filteredTeams);
              const clubName = this.getClubName(clubId);
              printSections.push({
                title: `${this.$tc("tournament.teams")} - ${clubName}`,
                html,
              });
            }
          }

          // Club-specific Judges
          if (selectedOptions.club.judges) {
            const filteredJudges = (this.referees || []).filter(
              (j) => parseInt(j.club?.id) === clubIdInt
            );
            if (filteredJudges.length > 0) {
              const html = this.generateJudgesTableHtml(filteredJudges);
              const clubName = this.getClubName(clubId);
              printSections.push({
                title: `${this.$tc("tournament.judges")} - ${clubName}`,
                html,
              });
            }
          }

          // Club-specific Officials - Always print, even if empty
          if (selectedOptions.club.officials) {
            const filteredOfficials = (this.officials || []).filter(
              (o) => parseInt(o.club?.id) === clubIdInt
            );
            // Always generate HTML (handles empty case)
            const html = this.generateOfficialsTableHtml(filteredOfficials);
            const clubName = this.getClubName(clubId);
            printSections.push({
              title: `${this.$tc("club.official", 2)} - ${clubName}`,
              html,
            });
          }

          // Club-specific Coaches
          if (selectedOptions.club.coaches) {
            const filteredCoaches = (this.coaches || []).filter(
              (c) => parseInt(c.club?.id) === clubIdInt
            );
            if (filteredCoaches.length > 0) {
              const html = this.generateCoachesTableHtml(filteredCoaches);
              const clubName = this.getClubName(clubId);
              printSections.push({
                title: `${this.$tc("tournament.coaches")} - ${clubName}`,
                html,
              });
            }
          }
        });
      }

      // 2. Global Competitors
      if (selectedOptions.globalCompetitors) {
        const element = this.$refs.printCompetitorsTable?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("tournament.competitors"),
            html: element.innerHTML,
          });
        }
      }

      // 3. Global Teams
      if (selectedOptions.globalTeams) {
        const element = this.$refs.printTeamsTable?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("tournament.teams"),
            html: element.innerHTML,
          });
        }
      }

      // 4. Global Judges
      if (selectedOptions.globalJudges) {
        const element = this.$refs.printRefereesTable?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("tournament.judges"),
            html: element.innerHTML,
          });
        }
      }

      // 5. Global Officials - Always print, even if empty
      if (selectedOptions.globalOfficials) {
        const element = this.$refs.printOfficialsTable?.$el;
        let html = "";

        // Try to get HTML from element first
        if (
          element &&
          element.innerHTML &&
          element.innerHTML.trim().length > 0
        ) {
          html = element.innerHTML;
        } else {
          // Generate HTML manually (handles empty case)
          html = this.generateOfficialsTableHtml(this.officials || []);
        }

        // Always add print section, even if empty
        printSections.push({
          title: this.$tc("club.official", 2),
          html,
        });
      }

      // 6. Global Coaches
      if (selectedOptions.globalCoaches) {
        const element = this.$refs.printCoachesTable?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("tournament.coaches"),
            html: element.innerHTML,
          });
        }
      }

      // 7. Basic Information
      if (selectedOptions.basicInformation) {
        const element = this.$refs.printInfoData?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("tournament.basic_information"),
            html: element.innerHTML,
          });
        }
      }

      // 8. Individual Disciplines
      if (
        selectedOptions.individualDisciplines?.checked &&
        selectedOptions.individualDisciplines.selectedCategory &&
        selectedOptions.individualDisciplines.selectedDiscipline
      ) {
        const category = this.allCategories.find(
          (c) =>
            String(c.id) ===
            String(selectedOptions.individualDisciplines.selectedCategory)
        );

        // Find discipline from competitors or tournament structure
        let discipline = null;
        if (this.competitors && this.competitors.length) {
          for (const competitor of this.competitors) {
            if (competitor.categories) {
              for (const cat of competitor.categories) {
                const catId = String(
                  cat.id || cat.categoryId || cat.category?.id || ""
                );
                if (
                  catId ===
                  String(selectedOptions.individualDisciplines.selectedCategory)
                ) {
                  if (cat.disciplines) {
                    discipline = cat.disciplines.find(
                      (d) =>
                        String(
                          d.id || d.disciplineId || d.discipline?.id || ""
                        ) ===
                        String(
                          selectedOptions.individualDisciplines
                            .selectedDiscipline
                        )
                    );
                    if (discipline) {
                      discipline = {
                        id:
                          discipline.id ||
                          discipline.disciplineId ||
                          discipline.discipline?.id,
                        name:
                          discipline.name ||
                          discipline.discipline?.name ||
                          discipline.disciplineName,
                      };
                      break;
                    }
                  }
                }
              }
              if (discipline) break;
            }
          }
        }

        // Also check tournament disciplines
        if (!discipline && this.tournament?.disciplines) {
          const disc = this.tournament.disciplines.find(
            (d) =>
              String(d.discipline?.id) ===
              String(selectedOptions.individualDisciplines.selectedDiscipline)
          );
          if (disc && disc.discipline) {
            discipline = {
              id: disc.discipline.id,
              name: disc.discipline.name,
            };
          }
        }

        if (category && discipline) {
          const categoryName = category?.name || "";
          const disciplineName = discipline?.name || "";
          const hasMenFilter = selectedOptions.individualDisciplines.men;
          const hasWomenFilter = selectedOptions.individualDisciplines.women;
          const hasAnyGenderFilter = hasMenFilter || hasWomenFilter;

          // Gender filters are optional - if none selected, print all competitors
          if (!hasAnyGenderFilter) {
            // No gender filter selected - print all competitors
            const competitors =
              getCompetitors(
                parseInt(
                  selectedOptions.individualDisciplines.selectedCategory
                ) || selectedOptions.individualDisciplines.selectedCategory,
                parseInt(discipline.id) || discipline.id,
                this.competitors || [],
                null // null = no gender filter, get all
              ) || [];
            printSections.push({
              title: `${categoryName} - ${disciplineName}`,
              html: this.generateDisciplineTable(competitors, false, null),
            });
          } else {
            // Gender filters are selected - apply them
            // Always add print sections, even if no competitors match the filter
            // This ensures print window opens even when filter returns empty results
            if (hasMenFilter) {
              const competitors =
                getCompetitors(
                  parseInt(
                    selectedOptions.individualDisciplines.selectedCategory
                  ) || selectedOptions.individualDisciplines.selectedCategory,
                  parseInt(discipline.id) || discipline.id,
                  this.competitors || [],
                  "M"
                ) || [];
              printSections.push({
                title: `${categoryName} - ${disciplineName} [${this.$tc(
                  "general.men"
                )}]`,
                html: this.generateDisciplineTable(competitors, false, true),
              });
            }
            if (hasWomenFilter) {
              const competitors =
                getCompetitors(
                  parseInt(
                    selectedOptions.individualDisciplines.selectedCategory
                  ) || selectedOptions.individualDisciplines.selectedCategory,
                  parseInt(discipline.id) || discipline.id,
                  this.competitors || [],
                  "F"
                ) || [];
              printSections.push({
                title: `${categoryName} - ${disciplineName} [${this.$tc(
                  "general.women"
                )}]`,
                html: this.generateDisciplineTable(competitors, false, false),
              });
            }
          }
        }
      }

      // 9. Team Disciplines
      if (
        selectedOptions.teamDisciplines?.checked &&
        selectedOptions.teamDisciplines.selectedCategory &&
        selectedOptions.teamDisciplines.selectedDiscipline
      ) {
        const category = this.allCategories.find(
          (c) =>
            String(c.id) ===
            String(selectedOptions.teamDisciplines.selectedCategory)
        );

        // Find discipline from teams or tournament structure
        let discipline = null;
        if (this.teams && this.teams.length) {
          for (const team of this.teams) {
            if (team.categories) {
              for (const cat of team.categories) {
                const catId = String(
                  cat.id || cat.categoryId || cat.category?.id || ""
                );
                if (
                  catId ===
                  String(selectedOptions.teamDisciplines.selectedCategory)
                ) {
                  if (cat.disciplines) {
                    discipline = cat.disciplines.find(
                      (d) =>
                        String(
                          d.id || d.disciplineId || d.discipline?.id || ""
                        ) ===
                        String(
                          selectedOptions.teamDisciplines.selectedDiscipline
                        )
                    );
                    if (discipline) {
                      discipline = {
                        id:
                          discipline.id ||
                          discipline.disciplineId ||
                          discipline.discipline?.id,
                        name:
                          discipline.name ||
                          discipline.discipline?.name ||
                          discipline.disciplineName,
                      };
                      break;
                    }
                  }
                }
              }
              if (discipline) break;
            }
          }
        }

        // Also check tournament teamDisciplines
        if (!discipline && this.tournament?.teamDisciplines) {
          const disc = this.tournament.teamDisciplines.find(
            (d) =>
              String(d.discipline?.id) ===
              String(selectedOptions.teamDisciplines.selectedDiscipline)
          );
          if (disc && disc.discipline) {
            discipline = {
              id: disc.discipline.id,
              name: disc.discipline.name,
            };
          }
        }

        if (category && discipline) {
          const categoryName = category?.name || "";
          const disciplineName = discipline?.name || "";
          const hasMenFilter = selectedOptions.teamDisciplines.men;
          const hasWomenFilter = selectedOptions.teamDisciplines.women;
          const hasAnyGenderFilter = hasMenFilter || hasWomenFilter;

          // Gender filters are optional - if none selected, print all teams
          if (!hasAnyGenderFilter) {
            // No gender filter selected - print all teams (MEN, WOMEN, MIX)
            const teams =
              getCompetitors(
                parseInt(selectedOptions.teamDisciplines.selectedCategory) ||
                  selectedOptions.teamDisciplines.selectedCategory,
                parseInt(discipline.id) || discipline.id,
                this.teams || [],
                null // null = no gender filter, get all
              ) || [];
            printSections.push({
              title: `${categoryName} - ${disciplineName}`,
              html: this.generateDisciplineTable(teams, true, null),
            });
          } else {
            // Gender filters are selected - apply them
            // Always add print sections, even if no teams match the filter
            // This ensures print window opens even when filter returns empty results
            if (hasMenFilter) {
              const teams = (
                getCompetitors(
                  parseInt(selectedOptions.teamDisciplines.selectedCategory) ||
                    selectedOptions.teamDisciplines.selectedCategory,
                  parseInt(discipline.id) || discipline.id,
                  this.teams || [],
                  null
                ) || []
              ).filter((t) => t && t.type === "MEN");
              printSections.push({
                title: `${categoryName} - ${disciplineName} [${this.$tc(
                  "general.men"
                )}]`,
                html: this.generateDisciplineTable(teams, true, true),
              });
            }
            if (hasWomenFilter) {
              const teams = (
                getCompetitors(
                  parseInt(selectedOptions.teamDisciplines.selectedCategory) ||
                    selectedOptions.teamDisciplines.selectedCategory,
                  parseInt(discipline.id) || discipline.id,
                  this.teams || [],
                  null
                ) || []
              ).filter((t) => t && t.type === "WOMEN");
              printSections.push({
                title: `${categoryName} - ${disciplineName} [${this.$tc(
                  "general.women"
                )}]`,
                html: this.generateDisciplineTable(teams, true, false),
              });
            }
          }
        }
      }

      // 10. Registration Fee
      if (selectedOptions.registrationFee) {
        const element = this.$refs.printRegistrationFeeByClub?.$el;
        if (element) {
          printSections.push({
            title: this.$tc("general.registration_fee"),
            html: element.innerHTML,
          });
        }
      }

      // Print all sections
      if (printSections.length > 0) {
        // Check if ALL sections are certificates
        const allAreCertificates = printSections.every(
          (section) => section.isCertificate === true
        );

        if (allAreCertificates) {
          // Use dedicated certificate print method (no titles, no headers)
          this.printCertificatesOnly(printSections);
        } else {
          // Use regular combined print method (with titles)
          this.printCombined(printSections);
        }

        // Close modal after print dialog is triggered
        this.$nextTick(() => {
          this.showPrintModal = false;
        });
      } else {
        // If no sections to print, close modal anyway
        this.showPrintModal = false;
      }
    },

    documentTypeUsesPlacementVariables(documentType) {
      const pattern = /\{\{(position|discipline|category)\}\}/;
      return (documentType.fields || []).some((f) =>
        pattern.test(f.template || "")
      );
    },

    async buildItemsForTarget(target, documentType) {
      if (target === "competitors") {
        if (this.documentTypeUsesPlacementVariables(documentType)) {
          const brackets = await this.$store
            .dispatch("tournaments/getBrackets", this.tournament.id)
            .catch(() => []);
          return this.buildDiplomaEntitiesFromBrackets(brackets || []);
        }
        const uniqueByCompetitor = new Map();
        (this.competitors || []).forEach((reg) => {
          if (!reg.competitor?.id) return;
          const cid = parseInt(reg.competitor.id, 10);
          if (!uniqueByCompetitor.has(cid)) {
            uniqueByCompetitor.set(cid, {
              competitor: reg.competitor,
              club: reg.club,
              registration: reg,
            });
          }
        });
        return Array.from(uniqueByCompetitor.values());
      }
      if (target === "clubs") {
        const uniqueClubsMap = new Map();
        (this.competitors || []).forEach((reg) => {
          if (reg.club?.id && !uniqueClubsMap.has(parseInt(reg.club.id))) {
            uniqueClubsMap.set(parseInt(reg.club.id), reg.club);
          }
        });
        (this.teams || []).forEach((team) => {
          if (team.club?.id && !uniqueClubsMap.has(parseInt(team.club.id))) {
            uniqueClubsMap.set(parseInt(team.club.id), team.club);
          }
        });
        return Array.from(uniqueClubsMap.values())
          .filter((club) => club && (club.name || club.shortName))
          .map((club) => ({ competitor: null, club, registration: null }));
      }
      if (target === "coaches")
        return this.mapPeopleToCertificateEntities(this.coaches || [], "coach");
      if (target === "officials")
        return this.mapPeopleToCertificateEntities(
          this.officials || [],
          "official"
        );
      if (target === "referees")
        return this.mapPeopleToCertificateEntities(
          this.referees || [],
          "referee"
        );
      return [];
    },

    // PrintDocuments renders <canvas> elements; canvas pixel data is not
    // serialized by innerHTML, so grabbing the wrap's raw HTML would print
    // blank pages. Convert each rendered canvas to a PNG <img> first, using
    // the same page structure printCertificatesOnly() already knows how to
    // paginate (.certificate-page / .certificate-page-image).
    captureDocumentPrintHtml() {
      const wrap = this.$refs.printDocumentsWrap;
      if (!wrap) return "";
      const canvases = wrap.querySelectorAll("canvas");
      let html = "";
      canvases.forEach((canvas, index) => {
        const dataUrl = canvas.toDataURL("image/png", 1.0);
        const breakStyle =
          index < canvases.length - 1
            ? "page-break-after: always;"
            : "page-break-after: auto;";
        html += `<div class="certificate-page" style="width: 210mm; height: 297mm; ${breakStyle}">`;
        html += `<img class="certificate-page-image" src="${dataUrl}" />`;
        html += "</div>";
      });
      return html;
    },

    // A fixed delay only lets Vue's reactivity settle before we look at the
    // canvases; it doesn't guarantee the (possibly slow) background image
    // has actually finished loading. PrintDocuments exposes a `loading`
    // boolean that stays true until its image's onload/onerror fires - poll
    // it after the fixed delay so a slow-but-eventually-successful load
    // can't get captured while the canvas is still blank. Resolves true
    // once loading has cleared, or false if it's still loading after
    // `extraTimeoutMs` on top of the caller's own delay.
    async waitForDocumentImageReady(extraTimeoutMs = 3000, pollIntervalMs = 100) {
      const start = Date.now();
      while (this.$refs.printDocumentsComponent?.loading) {
        if (Date.now() - start >= extraTimeoutMs) {
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }
      return true;
    },

    async handleDocumentPrint({ documentTypeId, targets, manual }) {
      const types = await this.$store.dispatch(
        "tournaments/getDocumentTypes",
        this.tournament.id
      );
      const documentType = types.find((t) => t.id === documentTypeId);
      if (!documentType) return;
      documentType.entityId = this.tournament.id;

      // PrintDocuments only fetches its background image in mounted() (no
      // watcher on the documentType prop). Force it to unmount/remount so a
      // second print in the same session re-fetches the correct image
      // instead of rendering the new type's fields onto the previous
      // type's cached one.
      this.currentDocumentType = null;
      await this.$nextTick();

      const printSections = [];
      let imageLoadFailed = false;

      const checkImageError = () => {
        if (this.$refs.printDocumentsComponent?.error) {
          imageLoadFailed = true;
        }
        return imageLoadFailed;
      };

      if (manual.enabled && manual.rows.length) {
        this.currentDocumentType = documentType;
        this.currentDocumentTarget = "competitors";
        this.documentPrintData = manual.rows.map((row) => ({
          manualValues: row,
        }));
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 300));
        const manualImageReady = await this.waitForDocumentImageReady();
        if (!manualImageReady) {
          imageLoadFailed = true;
        }
        if (manualImageReady && !checkImageError()) {
          const html = this.captureDocumentPrintHtml();
          if (html)
            printSections.push({
              title: `${documentType.name} - Manual`,
              html,
              isCertificate: true,
            });
        }
      } else {
        for (const [target, enabled] of Object.entries(targets)) {
          if (!enabled) continue;
          if (imageLoadFailed) break;
          const items = await this.buildItemsForTarget(target, documentType);
          if (!items.length) continue;
          this.currentDocumentType = documentType;
          this.currentDocumentTarget = target;
          this.documentPrintData = items;
          await this.$nextTick();
          await new Promise((resolve) => setTimeout(resolve, 500));
          const imageReady = await this.waitForDocumentImageReady();
          if (!imageReady) {
            imageLoadFailed = true;
            break;
          }
          if (checkImageError()) break;
          const html = this.captureDocumentPrintHtml();
          if (html)
            printSections.push({
              title: `${documentType.name} - ${target}`,
              html,
              isCertificate: true,
            });
        }
      }

      if (imageLoadFailed) {
        this.$message.error(
          `${
            this.$tc("tournament.document_image_load_failed") ||
            "Failed to load the document image for"
          } "${documentType.name}"`
        );
      } else if (printSections.length) {
        this.printCertificatesOnly(printSections);
      }
      this.showDocumentPrintModal = false;
    },

    mapPeopleToCertificateEntities(people, prefix) {
      const uniqueByPerson = new Map();
      people.forEach((person) => {
        if (!person) return;
        const personId =
          person.id != null
            ? person.id
            : `${prefix}-${person.firstName || ""}-${person.lastName || ""}`;
        const key = String(personId);
        if (!uniqueByPerson.has(key)) {
          uniqueByPerson.set(key, {
            competitor: {
              id: personId,
              firstName: person.firstName || "",
              lastName: person.lastName || "",
            },
            club: person.club || null,
            registration: {
              ...person,
              accreditationRole: prefix,
            },
          });
        }
      });
      return Array.from(uniqueByPerson.values());
    },

    getBracketRounds(bracket) {
      const matches = bracket.matches || [];
      const roundsMap = {};
      matches.forEach((match) => {
        if (!roundsMap[match.round]) {
          roundsMap[match.round] = { number: match.round, matches: [] };
        }
        roundsMap[match.round].matches.push(match);
      });
      Object.keys(roundsMap).forEach((roundNum) => {
        roundsMap[roundNum].matches.sort(
          (a, b) => (a.matchNumber || 0) - (b.matchNumber || 0)
        );
      });
      return Object.values(roundsMap).sort((a, b) => a.number - b.number);
    },

    getBracketCompetitors(bracket) {
      const competitors = [];
      const competitorIds = new Set();
      const rounds = this.getBracketRounds(bracket);
      if (rounds.length === 0) return [];
      const firstRound = rounds[0];
      firstRound.matches.forEach((match) => {
        if (match.competitorReg && !competitorIds.has(match.competitorRegId)) {
          competitors.push({
            ...match.competitorReg,
            _id: match.competitorRegId,
          });
          competitorIds.add(match.competitorRegId);
        }
        if (
          match.competitorTwoReg &&
          !competitorIds.has(match.competitorTwoRegId)
        ) {
          competitors.push({
            ...match.competitorTwoReg,
            _id: match.competitorTwoRegId,
          });
          competitorIds.add(match.competitorTwoRegId);
        }
      });
      return competitors;
    },

    isTeamBracket(bracket) {
      return (
        bracket.teamDiscipline !== null &&
        bracket.teamDiscipline !== undefined &&
        bracket.teamDiscipline !== "" &&
        bracket.teamDiscipline !== 0
      );
    },

    buildDiplomaEntitiesFromBrackets(brackets) {
      const placementKeys = ["first", "second", "third", "third2"];
      const placementLabels = { first: 1, second: 2, third: 3, third2: 3 };
      const byCompetitorId = new Map();

      const upsertEntity = (competitorId, entity) => {
        if (!competitorId) return;
        const key = String(competitorId);
        const existing = byCompetitorId.get(key);
        if (!existing) {
          byCompetitorId.set(key, entity);
          return;
        }
        const existingPlacement = existing.diplomaPlacement || 999;
        const newPlacement = entity.diplomaPlacement || 999;
        if (newPlacement < existingPlacement) {
          byCompetitorId.set(key, entity);
        }
      };

      brackets.forEach((bracket) => {
        const placement = bracket.placement || {};
        const placementMulti = bracket.placement_multi || {};

        const regIdsByKey = {};
        placementKeys.forEach((key) => {
          const ids = [];
          if (placement[key] != null && placement[key] !== "") {
            ids.push(placement[key]);
          }
          const multiIds = placementMulti[key];
          if (Array.isArray(multiIds) && multiIds.length > 0) {
            multiIds.forEach((id) => {
              if (id == null || id === "") return;
              ids.push(id);
            });
          }
          regIdsByKey[key] = Array.from(new Set(ids.map((v) => String(v)))).map(
            (v) => parseInt(v)
          );
        });

        const hasAnyWinner = placementKeys.some(
          (key) => regIdsByKey[key] && regIdsByKey[key].length > 0
        );
        if (!hasAnyWinner) return;

        const list = this.getBracketCompetitors(bracket);
        const discipline = bracket.discipline || bracket.teamDiscipline;
        const disciplineName = discipline?.name || "";
        const categoryName = bracket.category?.name || "";
        const isTeam = this.isTeamBracket(bracket);

        placementKeys.forEach((key) => {
          const placementNum = placementLabels[key];
          const ids = regIdsByKey[key] || [];
          if (ids.length === 0) return;

          ids.forEach((regId) => {
            if (regId == null || regId === "") return;
            const reg = list.find(
              (c) => c._id != null && String(c._id) === String(regId)
            );
            if (!reg) return;

            if (
              isTeam &&
              reg.isTeam &&
              reg.teamMembers &&
              reg.teamMembers.length > 0
            ) {
              reg.teamMembers.forEach((member) => {
                const competitorId = member.competitorId ?? member.id;
                upsertEntity(competitorId, {
                  competitor: {
                    id: competitorId,
                    firstName: member.firstName || "",
                    lastName: member.lastName || "",
                  },
                  club: reg.club || null,
                  registration: reg,
                  diplomaPlacement: placementNum,
                  diplomaDiscipline: disciplineName,
                  diplomaCategory: categoryName,
                });
              });
            } else if (reg.competitor && reg.competitor.id != null) {
              const competitorId = reg.competitor.id;
              upsertEntity(competitorId, {
                competitor: reg.competitor || null,
                club: reg.club || null,
                registration: reg,
                diplomaPlacement: placementNum,
                diplomaDiscipline: disciplineName,
                diplomaCategory: categoryName,
              });
            }
          });
        });
      });
      return Array.from(byCompetitorId.values());
    },

    generateCertificatesHtml(entities, certificateType, target) {
      // Legacy method - kept for backward compatibility
      // This should not be used anymore, but kept as fallback
      if (!entities || entities.length === 0) {
        return (
          '<div style="padding: 40px; text-align: center; color: #909399;">' +
          this.$tc("general.no_data") +
          "</div>"
        );
      }

      let html = "";
      entities.forEach((entity, index) => {
        if (index > 0) {
          html += '<div style="page-break-before: always;"></div>';
        }
        html +=
          '<div style="min-height: 600px; padding: 40px; border: 2px solid #e4e7ed; text-align: center;">';
        html += '<div style="margin-bottom: 40px;">';
        html +=
          '<h2 style="font-size: 28px; font-weight: bold; margin-bottom: 10px;">' +
          (this.tournament?.name || "") +
          "</h2>";

        const typeLabels = {
          certificate: this.$tc("tournament.certificate"),
          diploma: this.$tc("tournament.diploma"),
          thank_you_letter: this.$tc("tournament.thank_you_letter"),
        };
        html +=
          '<h3 style="font-size: 22px; font-weight: 500; color: #606266;">' +
          (typeLabels[certificateType] || "") +
          "</h3>";
        html += "</div>";

        html += '<div style="margin: 60px 0;">';
        html +=
          '<p style="font-size: 16px; line-height: 1.8; color: #303133; margin-bottom: 40px;">';
        if (target === "competitors" && entity.competitor) {
          html +=
            this.$tc("tournament.certificate_text_competitor", 1) ||
            "Ovaj sertifikat potvrđuje učešće na turniru.";
        } else if (target === "clubs" && entity.club) {
          html +=
            this.$tc("tournament.certificate_text_club", 1) ||
            "Ovaj sertifikat potvrđuje učešće sportskog udruženja na turniru.";
        }
        html += "</p>";

        html += '<div style="margin-top: 60px;">';
        if (target === "competitors" && entity.competitor) {
          html +=
            '<p style="font-size: 24px; font-weight: bold; color: #303133; margin-bottom: 10px;">';
          html +=
            entity.competitor.firstName + " " + entity.competitor.lastName;
          html += "</p>";
          if (entity.club) {
            html +=
              '<p style="font-size: 18px; color: #606266;">' +
              (entity.club.shortName || entity.club.name || "") +
              "</p>";
          }
        } else if (target === "clubs" && entity.club) {
          html +=
            '<p style="font-size: 24px; font-weight: bold; color: #303133; margin-bottom: 10px;">';
          html += entity.club.name || "";
          html += "</p>";
          if (entity.club.shortName) {
            html +=
              '<p style="font-size: 18px; color: #606266;">' +
              entity.club.shortName +
              "</p>";
          }
        }
        html += "</div>";
        html += "</div>";

        html +=
          '<div style="margin-top: 80px; padding-top: 20px; border-top: 1px solid #e4e7ed;">';
        html +=
          '<p style="font-size: 14px; color: #909399;">' +
          new Date().toLocaleDateString("sr-RS", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }) +
          "</p>";
        html += "</div>";
        html += "</div>";
      });
      return html;
    },

    generateDisciplineTable(data, isTeam, isMale) {
      // Always return table structure, even if data is empty
      // This ensures print window opens even when filter returns no results
      let html = "<table><thead><tr><th>" + this.$tc("general.n0") + "</th>";
      if (!isTeam) {
        html += "<th>" + this.$tc("form.image") + "</th>";
      }
      html += "<th>" + this.$tc("form.name") + "</th>";
      if (!isTeam) {
        html += "<th>" + this.$tc("form.belt") + "</th>";
        html += "<th>" + this.$tc("form.date_of_birth") + "</th>";
      }
      html += "<th>" + this.$tc("club.short_name") + "</th>";
      html +=
        '<th class="placement-header">' +
        this.$tc("general.placement") +
        "</th></tr></thead><tbody>";

      if (data && data.length > 0) {
        data.forEach((item, index) => {
          html += "<tr><td>" + (index + 1) + ".</td>";
          if (!isTeam && item.competitor) {
            html +=
              '<td style="width: 80px"><div class="image"><img src="' +
              escapeHtml(this.$file("avatars", item.competitor.avatar)) +
              '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
            html +=
              "<td>" +
              escapeHtml(item.competitor.firstName || "") +
              " " +
              escapeHtml(item.competitor.lastName || "") +
              "</td>";
            html += "<td>" + escapeHtml(item.competitor.belt || "") + "</td>";
            html +=
              "<td>" +
              (item.competitor.born
                ? new Date(item.competitor.born).toLocaleDateString()
                : "") +
              "</td>";
          } else if (isTeam) {
            html += "<td>" + escapeHtml(getCompetitorNames(item, true) || "") + "</td>";
          }
          if (item.club) {
            const logoHtml = item.club.logo
              ? '<span class="club-logo-cell"><img src="' +
                escapeHtml(this.$file("avatars", item.club.logo)) +
                '" class="club-logo" alt="" /></span>'
              : "";
            html +=
              "<td>" +
              logoHtml +
              " " +
              escapeHtml(item.club.shortName) +
              ", " +
              escapeHtml(item.club.city) +
              "</td>";
          } else {
            html += "<td></td>";
          }
          html +=
            '<td class="placement-cell"><span class="placement-box"></span></td>';
          html += "</tr>";
        });
      }
      // If no data, table will have empty tbody (still valid HTML structure)

      html += "</tbody></table>";
      return html;
    },

    printCertificatesOnly(sections) {
      // Dedicated method for printing ONLY certificates - no titles, no headers, no footers
      if (!sections || sections.length === 0) {
        return;
      }

      const allAreAccreditations = sections.every(
        (section) => section.isAccreditation === true
      );
      const pageSize = allAreAccreditations ? "148mm 105mm" : "A4";

      // Build HTML with ONLY certificate images - no titles, no headers
      let certificatesHtml = "";
      sections.forEach((section) => {
        certificatesHtml += section.html;
      });

      // Create dedicated print window with minimal HTML structure
      const newWin = window.open("");
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>Certificates</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .certificate-page {
              margin: 0;
              padding: 0;
              display: flex;
              align-items: stretch;
              justify-content: stretch;
              overflow: hidden;
            }
            .certificate-page-image {
              width: 100%;
              height: 100%;
              object-fit: contain;
              display: block;
            }
            @media print {
              @page {
                size: ${pageSize};
                margin: 0;
              }
              body {
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          </style>
        </head>
        <body>
          ${certificatesHtml}
        </body>
        </html>
      `;
      newWin.document.write(htmlContent);
      newWin.document.close();

      // Wait for images to load before printing
      setTimeout(() => {
        newWin.print();
        newWin.close();
      }, 500);
    },

    printCombined(sections) {
      // Method for printing tables, brackets, and other content (with titles)
      if (!sections || sections.length === 0) {
        return;
      }

      const title = this.tournament?.name || this.$tc("general.print");
      let combinedHtml = "";

      sections.forEach((section, index) => {
        if (index > 0) {
          combinedHtml +=
            '<div style="page-break-before: always; margin-top: 40px;"></div>';
        }
        combinedHtml += "<h2>" + section.title + "</h2>";
        combinedHtml += section.html;
      });

      // Create a temporary element to use with openPrint
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = combinedHtml;

      // Ensure print dialog opens - use setTimeout to ensure DOM is ready
      setTimeout(() => {
        openPrint(title, tempDiv);
      }, 100);
    },

    getClubName(clubId) {
      const club = this.clubs?.find((c) => parseInt(c.id) === parseInt(clubId));
      return club?.shortName || club?.name || "";
    },

    generateClubTableHtml(clubs) {
      if (!clubs || clubs.length === 0) return "";

      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("settings.logo") + "</th>";
      html += "<th>" + this.$tc("club.club_name") + "</th>";
      html += "<th>" + this.$tc("club.short_name") + "</th>";
      html += "<th>" + this.$tc("form.city") + "</th>";
      html += "<th>" + this.$tc("form.address") + "</th>";
      html += "<th>" + this.$tc("form.email") + "</th>";
      html += "<th>" + this.$tc("form.website") + "</th>";
      html += "<th>" + this.$tc("form.contact_person") + "</th>";
      html += "<th>" + this.$tc("form.mobile") + "</th>";
      html += "</tr></thead><tbody>";

      clubs.forEach((club, index) => {
        html += "<tr>";
        html += "<td>" + (index + 1) + ".</td>";
        html +=
          '<td style="width: 80px"><div class="image"><img src="' +
          this.$file("avatars", club.logo) +
          '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
        html += "<td>" + (club.name || "") + "</td>";
        html += "<td>" + (club.shortName || "") + "</td>";
        html += "<td>" + (club.city || "") + "</td>";
        html += "<td>" + (club.address || "") + "</td>";
        html += "<td>" + (club.email || "") + "</td>";
        html += "<td>" + (club.website || "") + "</td>";
        html += "<td>" + (club.contact || "") + "</td>";
        html += "<td>" + (club.mobile || "") + "</td>";
        html += "</tr>";
      });

      html += "</tbody></table>";
      return html;
    },

    generateCompetitorsTableHtml(competitors) {
      if (!competitors || competitors.length === 0) return "";

      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("form.image") + "</th>";
      html += "<th>" + this.$tc("form.name") + "</th>";
      html += "<th>" + this.$tc("form.date_of_birth") + "</th>";
      html += "<th>" + this.$tc("form.gender") + "</th>";
      html += "<th>" + this.$tc("form.belt") + "</th>";
      html += "<th>" + this.$tc("general.club") + "</th>";
      html += "<th>" + this.$tc("settings.discipline") + "</th>";
      html += "</tr></thead><tbody>";

      competitors.forEach((registration, index) => {
        html += "<tr>";
        html += "<td>" + (index + 1) + ".</td>";
        html +=
          '<td style="width: 80px"><div class="image"><img src="' +
          this.$file("avatars", registration.competitor.avatar) +
          '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
        html +=
          "<td>" +
          (registration.assignedNumber
            ? `#${registration.assignedNumber} `
            : "") +
          registration.competitor.firstName +
          " " +
          registration.competitor.lastName +
          (registration.is_seeded ? " 🏅" : "") +
          "</td>";
        html +=
          "<td>" +
          (registration.competitor.born
            ? new Date(registration.competitor.born).toLocaleDateString()
            : "") +
          "</td>";
        html +=
          "<td>" +
          (registration.competitor.isMale
            ? this.$tc("general.men")
            : this.$tc("general.women")) +
          "</td>";
        html += "<td>" + (registration.competitor.belt || "") + "</td>";
        html += "<td>" + (registration.club?.shortName || "") + "</td>";
        html += "<td>";
        if (registration.categories) {
          registration.categories.forEach((cat) => {
            if (cat.category) html += "<div>" + cat.category.name + "</div>";
            if (cat.disciplines) {
              cat.disciplines.forEach((disc) => {
                if (disc.discipline) {
                  html += "<div>" + disc.discipline.name + "</div>";
                }
              });
            }
          });
        }
        html += "</td>";
        html += "</tr>";
      });

      html += "</tbody></table>";
      return html;
    },

    generateTeamsTableHtml(teams) {
      if (!teams || teams.length === 0) return "";

      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("form.name") + "</th>";
      html += "<th>" + this.$tc("general.club") + "</th>";
      html += "<th>" + this.$tc("settings.discipline") + "</th>";
      html += "</tr></thead><tbody>";

      teams.forEach((team, index) => {
        html += "<tr>";
        html += "<td>" + (index + 1) + ".</td>";
        html += "<td>" + (team.name || "") + "</td>";
        html += "<td>" + (team.club?.shortName || "") + "</td>";
        html += "<td>";
        if (team.categories) {
          team.categories.forEach((cat) => {
            if (cat.category) html += "<div>" + cat.category.name + "</div>";
            if (cat.disciplines) {
              cat.disciplines.forEach((disc) => {
                if (disc.discipline) {
                  html += "<div>" + disc.discipline.name + "</div>";
                }
              });
            }
          });
        }
        html += "</td>";
        html += "</tr>";
      });

      html += "</tbody></table>";
      return html;
    },

    generateJudgesTableHtml(judges) {
      if (!judges || judges.length === 0) return "";

      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("form.image") + "</th>";
      html += "<th>" + this.$tc("form.name") + "</th>";
      html += "<th>" + this.$tc("club.club_name") + "</th>";
      if (this.$config.isWff) {
        html += "<th>" + this.$tc("form.no_certificate_wff") + "</th>";
        html += "<th>" + this.$tc("form.no_referee_certificate_wff") + "</th>";
        html += "<th>" + this.$tc("form.no_fudokan_passport") + "</th>";
      }
      html += "<th>" + this.$tc("form.day") + "</th>";
      html += "<th>" + this.$tc("club.referee_rang") + "</th>";
      html += "</tr></thead><tbody>";

      judges.forEach((judge, index) => {
        html += "<tr>";
        html += "<td>" + (index + 1) + ".</td>";
        html +=
          '<td style="width: 80px"><div class="image"><img src="' +
          this.$file("avatars", judge.avatar) +
          '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
        html += "<td>" + judge.firstName + " " + judge.lastName + "</td>";
        html += "<td>" + (judge.club?.shortName || "") + "</td>";
        if (this.$config.isWff) {
          html += "<td>" + (judge.noCertificateWff || "") + "</td>";
          html += "<td>" + (judge.noRefereeCertificateWff || "") + "</td>";
          html += "<td>" + (judge.noFudokanPassport || "") + "</td>";
        }
        html += "<td>" + (judge.dan || "") + "</td>";
        html +=
          "<td>" +
          (judge.rang === "OTHER" ? judge.rangOther || "" : judge.rang || "") +
          "</td>";
        html += "</tr>";
      });

      html += "</tbody></table>";
      return html;
    },

    generateOfficialsTableHtml(officials) {
      // Always return a valid table structure, even if empty
      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("form.image") + "</th>";
      html += "<th>" + this.$tc("form.name") + "</th>";
      html += "<th>" + this.$tc("club.club_name") + "</th>";
      html += "<th>" + this.$tc("general.function") + "</th>";
      html += "</tr></thead><tbody>";

      if (officials && officials.length > 0) {
        officials.forEach((official, index) => {
          html += "<tr>";
          html += "<td>" + (index + 1) + ".</td>";
          html +=
            '<td style="width: 80px"><div class="image"><img src="' +
            this.$file("avatars", official.avatar) +
            '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
          html +=
            "<td>" + official.firstName + " " + official.lastName + "</td>";
          html += "<td>" + (official.club?.shortName || "") + "</td>";
          html += "<td>" + (official.funct || "") + "</td>";
          html += "</tr>";
        });
      } else {
        // Add empty state row
        html += "<tr>";
        html +=
          '<td colspan="5" style="text-align: center; padding: 20px; color: #909399;">';
        html += this.$tc("general.no_data") || "No officials registered";
        html += "</td>";
        html += "</tr>";
      }

      html += "</tbody></table>";
      return html;
    },

    generateCoachesTableHtml(coaches) {
      if (!coaches || coaches.length === 0) return "";

      let html = "<table><thead><tr>";
      html += "<th>" + this.$tc("general.n0") + "</th>";
      html += "<th>" + this.$tc("form.image") + "</th>";
      html += "<th>" + this.$tc("form.name") + "</th>";
      html += "<th>" + this.$tc("club.club_name") + "</th>";
      if (this.$config.isWff) {
        html += "<th>" + this.$tc("form.no_certificate_wff") + "</th>";
        html += "<th>" + this.$tc("form.no_fudokan_passport") + "</th>";
      }
      html += "</tr></thead><tbody>";

      coaches.forEach((coach, index) => {
        html += "<tr>";
        html += "<td>" + (index + 1) + ".</td>";
        html +=
          '<td style="width: 80px"><div class="image"><img src="' +
          this.$file("avatars", coach.avatar) +
          '" style="width: 75px; height: 75px; object-fit: cover;" /></div></td>';
        html += "<td>" + coach.firstName + " " + coach.lastName + "</td>";
        html += "<td>" + (coach.club?.shortName || "") + "</td>";
        if (this.$config.isWff) {
          html += "<td>" + (coach.noCertificateWff || "") + "</td>";
          html += "<td>" + (coach.noFudokanPassport || "") + "</td>";
        }
        html += "</tr>";
      });

      html += "</tbody></table>";
      return html;
    },
  },
};
</script>

<style lang="scss" scoped>
.cover {
  width: 100%;
  margin-bottom: 15px;
  img {
    width: 100%;
  }
}

.description {
  margin: 0;
}

.gallery {
  display: flex;
  overflow-x: auto;
  .image {
    position: relative;
    border: 4px solid transparent;
    img {
      display: block;
      cursor: pointer;
      height: 100px;
      transition: all ease 0.2s;
    }
    .delete {
      opacity: 0;
      position: absolute;
      top: 0;
      right: 0;
      width: 40px;
      height: 40px;
      color: #fff;
      background-color: rgba(0, 0, 0, 0.6);
      transition: all ease 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover {
        cursor: pointer;
        background-color: rgba(0, 0, 0, 0.8);
      }
    }

    &:hover {
      border-color: $primary;
      .delete {
        opacity: 1;
      }
    }
  }

  .image + .image {
    margin-left: 10px;
  }
}

.documents {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
